import * as actions from './actions';

import { catchError, filter, groupBy, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';

import { AjaxResponse } from 'rxjs/ajax';
import { Epic } from 'redux-observable';
import { Observable, of } from 'rxjs';
import { RootAction } from 'store/actions';
import { RootDependencies } from 'store/dependencies';
import { RootState } from 'store/reducer';
import { isActionOf } from 'typesafe-actions';
import { transformErrorIntoSerializableError } from 'store/utils';

export const toggleResourceFavoriteEpic: Epic<RootAction, RootAction, RootState, RootDependencies> = (
  action$,
  state$,
  { apiClient },
) =>
  action$.pipe(
    filter(isActionOf(actions.toggleResourceFavorite.request)),
    // Grouped by resource so favoriting resource A and un-favoriting resource B at the same time run
    // concurrently — a single `switchMap` over the whole stream would cancel (and abort the in-flight
    // XHR for) resource A's request the moment resource B's action arrived. Within one resource,
    // `switchMap` still applies: a second rapid toggle on the *same* resource cancels the first, since
    // only the latest desired state for that resource matters and the backend toggle is idempotent.
    groupBy(action => action.payload.resourceUuid),
    mergeMap(resourceRequests$ =>
      resourceRequests$.pipe(
        withLatestFrom(state$),
        switchMap(([{ payload }, state]) => {
          const { ownerType, resourceUuid, nextIsFavorite } = payload;
          const request: Observable<AjaxResponse<unknown>> = nextIsFavorite
            ? apiClient(state).addResourceFavorite({ ownerType, resourceUuid })
            : apiClient(state).removeResourceFavorite({ ownerType, resourceUuid });

          return request.pipe(
            map(() => actions.toggleResourceFavorite.success({ resourceUuid, isFavorite: nextIsFavorite })),
            catchError((error: Error) =>
              of(
                actions.toggleResourceFavorite.failure({
                  error: transformErrorIntoSerializableError(error),
                  resourceUuid,
                  isFavorite: !nextIsFavorite,
                }),
              ),
            ),
          );
        }),
      ),
    ),
  );
